import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Badge,
  Button,
  Spinner,
  Form,
  Row,
  Col,
  Modal,
  Dropdown,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { formatDistance } from "date-fns";
import pt from "date-fns/locale/pt-BR";
import {
  IconEye,
  IconEdit,
  IconTrash,
  IconPlus,
  IconFileDownload,
  IconFilter,
  IconTag,
  IconUsers,
  IconPhone,
  IconMail,
  IconMapPin,
  IconCalendar,
  IconChartBar,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import {
  listCustomers,
  deleteCustomer,
  bulkUpdateCustomers,
  exportCustomers,
} from "./services/CustomerServices";
import CustomerModal from "./components/CustomerModal";
import CustomerDetails from "./components/CustomerDetails";
import CustomerStatsModal from "./components/CustomerStatsModal";
import { formatPhone } from "./utils";
import { createPortal } from "react-dom";
import styled from "styled-components";
import DatatablePaginated from "../../jsx/components/DatatablePaginated";
import NotificationsContext from "../../contexts/NotificationsContext";

const CustomerList = () => {
  const { setNotify } = useContext(NotificationsContext);

  // Estados principais
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);

  // Estados dos modais
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Estados de filtros
  const [filters, setFilters] = useState({
    search: "",
    person_type: "",
    has_email: "",
    has_whatsapp: "",
    city: "",
    state: "",
    page: 1,
    page_size: 15,
    ordering: "-created_at",
  });

  useEffect(() => {
    fetchCustomers();
  }, [filters]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await listCustomers(filters);
      setCustomers(response.data.results || []);
      setTotalRows(response.data.count || 0);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
      setNotify({
        title: "Erro!",
        message: "Erro ao carregar lista de clientes",
        type: "error",
        visible: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1, // Reset página quando filtros mudam
    }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const handleCreateCustomer = () => {
    setSelectedCustomer(null);
    setIsEditMode(false);
    setShowCustomerModal(true);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setIsEditMode(true);
    setShowCustomerModal(true);
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
  };

  const handleDeleteCustomer = (customer) => {
    Swal.fire({
      title: "Tem certeza?",
      text: `Deseja inativar o cliente "${customer.full_name}"? Esta ação pode ser revertida.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, inativar!",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteCustomer(customer.id);
          setNotify({
            title: "Sucesso!",
            message: "Cliente inativado com sucesso!",
            type: "success",
            visible: true,
          });
          fetchCustomers();
        } catch (error) {
          setNotify({
            title: "Erro!",
            message: "Erro ao inativar cliente",
            type: "error",
            visible: true,
          });
        }
      }
    });
  };

  const handleBulkUpdate = async (updateData) => {
    if (selectedRows.length === 0) {
      Swal.fire("Atenção!", "Selecione pelo menos um cliente.", "info");
      return;
    }

    try {
      const customerIds = selectedRows.map((row) => row.id);
      await bulkUpdateCustomers({
        customer_ids: customerIds,
        ...updateData,
      });

      setNotify({
        title: "Sucesso!",
        message: `${selectedRows.length} cliente(s) atualizado(s) com sucesso!`,
        type: "success",
        visible: true,
      });

      setSelectedRows([]);
      fetchCustomers();
    } catch (error) {
      setNotify({
        title: "Erro!",
        message: "Erro na atualização em lote",
        type: "error",
        visible: true,
      });
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportCustomers(filters);

      // Criar blob e download
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clientes_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setNotify({
        title: "Sucesso!",
        message: "Relatório exportado com sucesso!",
        type: "success",
        visible: true,
      });
    } catch (error) {
      setNotify({
        title: "Erro!",
        message: "Erro ao exportar relatório",
        type: "error",
        visible: true,
      });
    }
  };

  const onSelectedRowsChange = (state) => {
    setSelectedRows(state.selectedRows || []);
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "success",
      inactive: "secondary",
      blocked: "danger",
      potential: "warning",
    };

    const labels = {
      active: "Ativo",
      inactive: "Inativo",
      blocked: "Bloqueado",
      potential: "Potencial",
    };

    return (
      <Badge bg={variants[status] || "secondary"}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getPersonTypeBadge = (personType) => {
    return (
      <Badge bg={personType === "PF" ? "info" : "primary"}>
        {personType === "PF" ? "Pessoa Física" : "Pessoa Jurídica"}
      </Badge>
    );
  };

  const DropdownPortal = ({ children, show }) => {
    if (!show) return null;

    return createPortal(
      <div style={{ position: "absolute", zIndex: 9999 }}>{children}</div>,
      document.body
    );
  };

  const columns = [
    {
      name: "Nome",
      selector: (row) => row.full_name,
      sortable: true,
      wrap: true,
      minWidth: "200px",
      cell: (row) => (
        <div>
          <div className="fw-bold">{row.full_name}</div>
          {row.nickname && (
            <small className="text-muted">({row.nickname})</small>
          )}
        </div>
      ),
    },
    {
      name: "Contato",
      selector: (row) => row.phone_primary,
      sortable: true,
      minWidth: "150px",
      cell: (row) => (
        <div>
          <div className="d-flex align-items-center">
            <IconPhone size={14} className="me-1 text-muted" />
            {formatPhone(row.phone_primary)}
          </div>
          {row.email && (
            <div className="d-flex align-items-center mt-1">
              <IconMail size={14} className="me-1 text-muted" />
              <small className="text-truncate" style={{ maxWidth: "120px" }}>
                {row.email}
              </small>
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Tipo",
      selector: (row) => row.person_type,
      sortable: true,
      minWidth: "120px",
      cell: (row) => (
        <div>
          {getPersonTypeBadge(row.person_type)}
          <div className="mt-1">
            <small className="text-muted">
              {row.person_type === "PF" && row.cpf_formatted}
              {row.person_type === "PJ" && row.cnpj_formatted}
            </small>
          </div>
        </div>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      minWidth: "100px",
      cell: (row) => getStatusBadge(row.status),
    },

    {
      name: "Cadastro",
      selector: (row) => row.created_at,
      sortable: true,
      minWidth: "120px",
      cell: (row) => new Date(row.created_at).toLocaleString(),
    },
    {
      name: "Ações",
      cell: (row) => (
        <Dropdown>
          <Dropdown.Toggle
            variant="outline-primary"
            size="sm"
            id={`dropdown-split-${row.id}`}
          />

          <DropdownPortal show={true}>
            <Dropdown.Menu style={{ zIndex: 9999 }}>
              <Dropdown.Item onClick={() => handleViewCustomer(row)}>
                <IconEye size={16} className="me-2" />
                Ver informações
              </Dropdown.Item>

              <Dropdown.Divider />
              <Dropdown.Item
                className="text-danger"
                onClick={() => handleDeleteCustomer(row)}
              >
                <IconTrash size={16} className="me-2" />
                Inativar
              </Dropdown.Item>
            </Dropdown.Menu>
          </DropdownPortal>
        </Dropdown>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "100px",
    },
  ];

  return (
    <div className="main-container">
      <Card>
        <CardHeaderWrapper className="border-bottom">
          <Row className="d-flex w-100">
            <Col xs={12} md={6}>
              <h4>
                <IconUsers className="me-2" />
                Clientes
                {totalRows > 0 && (
                  <Badge bg="primary" className="ms-2">
                    {totalRows}
                  </Badge>
                )}
              </h4>
            </Col>
            <Col xs={12} md={6}>
              <ButtonGroup className="me-2">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Estatísticas</Tooltip>}
                >
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => setShowStatsModal(true)}
                  >
                    <IconChartBar size={18} />
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Exportar</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleExport}
                  >
                    <IconFileDownload size={18} />
                  </Button>
                </OverlayTrigger>
              </ButtonGroup>

              <Button
                variant="primary"
                size="sm"
                onClick={handleCreateCustomer}
                className="d-inline-flex align-items-center"
              >
                <IconPlus size={18} className="me-1" />
                <span className="d-none d-sm-inline">Novo Cliente</span>
                <span className="d-sm-none">Novo</span>
              </Button>
            </Col>
          </Row>
        </CardHeaderWrapper>

        <Card.Body className="p-0 p-md-3">
          {/* Filtros */}
          <Row className="mb-3 px-3 px-md-0">
            <Col xs={12} md={3} className="mb-2">
              <Form.Control
                type="text"
                placeholder="Buscar por nome ou telefone..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                size="sm"
              />
            </Col>

            <Col xs={6} md={2} className="mb-2">
              <Form.Select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                size="sm"
              >
                <option value="">Todos Status</option>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
                <option value="blocked">Bloqueado</option>
                <option value="potential">Potencial</option>
              </Form.Select>
            </Col>

            <Col xs={6} md={2} className="mb-2">
              <Form.Select
                value={filters.person_type}
                onChange={(e) =>
                  handleFilterChange("person_type", e.target.value)
                }
                size="sm"
              >
                <option value="">Tipo Pessoa</option>
                <option value="PF">Pessoa Física</option>
                <option value="PJ">Pessoa Jurídica</option>
              </Form.Select>
            </Col>

            <Col xs={6} md={2} className="mb-2">
              <Form.Select
                value={filters.customer_type}
                onChange={(e) =>
                  handleFilterChange("customer_type", e.target.value)
                }
                size="sm"
              >
                <option value="">Tipo Cliente</option>
                <option value="new">Novo</option>
                <option value="regular">Regular</option>
                <option value="vip">VIP</option>
                <option value="returning">Retornando</option>
              </Form.Select>
            </Col>

            <Col xs={6} md={3} className="mb-2">
              <Form.Select
                value={filters.acquisition_channel}
                onChange={(e) =>
                  handleFilterChange("acquisition_channel", e.target.value)
                }
                size="sm"
              >
                <option value="">Canal de Aquisição</option>
                <option value="website">Site/Website</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
                <option value="google">Google</option>
                <option value="referral">Indicação</option>
                <option value="walk_in">Entrada Direta</option>
                <option value="other">Outro</option>
              </Form.Select>
            </Col>
          </Row>

          {/* Ações em lote */}
          {selectedRows.length > 0 && (
            <Row className="mb-3 px-3 px-md-0">
              <Col>
                <div className="d-flex flex-wrap align-items-center bg-light p-2 rounded">
                  <span className="me-3">
                    <strong>{selectedRows.length}</strong> cliente(s)
                    selecionado(s)
                  </span>

                  <ButtonGroup size="sm">
                    <Button
                      variant="outline-success"
                      onClick={() => handleBulkUpdate({ status: "active" })}
                    >
                      Ativar
                    </Button>
                    <Button
                      variant="outline-warning"
                      onClick={() => handleBulkUpdate({ status: "inactive" })}
                    >
                      Inativar
                    </Button>
                    <Button
                      variant="outline-info"
                      onClick={() =>
                        handleBulkUpdate({ accepts_marketing: true })
                      }
                    >
                      Aceitar Marketing
                    </Button>
                  </ButtonGroup>
                </div>
              </Col>
            </Row>
          )}

          {/* Tabela */}
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Carregando clientes...</p>
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center p-5">
              <IconUsers size={48} className="text-muted mb-3" />
              <h5 className="text-muted">Nenhum cliente encontrado</h5>
              <p className="text-muted">
                {filters.search || filters.status || filters.person_type
                  ? "Nenhum cliente corresponde aos filtros selecionados."
                  : "Comece criando seu primeiro cliente!"}
              </p>
              {!filters.search && !filters.status && !filters.person_type && (
                <Button variant="primary" onClick={handleCreateCustomer}>
                  <IconPlus size={18} className="me-1" />
                  Criar Primeiro Cliente
                </Button>
              )}
            </div>
          ) : (
            <DatatablePaginated
              componentName="customer-list"
              data={customers}
              columns={columns}
              totalRows={totalRows}
              handlePageChange={handlePageChange}
              pagination={true}
              paginationServer={true}
              paginationTotalRows={totalRows}
              paginationPerPage={filters.page_size}
              paginationDefaultPage={filters.page}
              isLoading={loading}
              selectable={true}
              filterByField={false}
              onSelectedRowsChange={onSelectedRowsChange}
              classes="px-0"
            />
          )}
        </Card.Body>
      </Card>

      {/* Modal de Cadastro/Edição */}
      <CustomerModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        customer={selectedCustomer}
        isEdit={isEditMode}
        onSave={fetchCustomers}
      />

      {/* Modal de Detalhes */}
      <CustomerDetails
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        customer={selectedCustomer}
        onEdit={handleEditCustomer}
        onDelete={handleDeleteCustomer}
      />

      {/* Modal de Estatísticas */}
      <CustomerStatsModal
        show={showStatsModal}
        onHide={() => setShowStatsModal(false)}
      />
    </div>
  );
};

const CardHeaderWrapper = styled(Card.Header)`
  .row div:nth-child(2) {
    display: flex;
    justify-content: end;
  }
  @media (max-width: 768px) {
    .row {
      text-align: center;
      gap: 20px;
    }
    .row div:nth-child(2) {
      display: flex;
      justify-content: center;
    }
  }
`;

export default CustomerList;
